// Serviço de Autenticação - v3.0.1

import API_URL from '../config';
import { flushDraftToStorage } from '../utils/ncDraftStore'; // #145/#215/#271

class AuthService {
  // Login
  async login(email, password) {
const params = new URLSearchParams();
params.append('email', email);
params.append('password', password);

const response = await fetch(`${API_URL}/auth/login`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  body: params
});

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erro ao fazer login');
    }

    const data = await response.json();
    
    // Salvar tokens — access + refresh (Sessão 17 C)
    localStorage.setItem('token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }

    // Buscar dados do usuário
    const user = await this.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(user));

    return user;
  }

  // Cadastro público self-service (#79). Cria tenant + 1º admin + aceite de
  // termos no backend e já devolve o par de tokens — fazemos auto-login pelo
  // MESMO fluxo do login (persiste tokens, busca /auth/me, cacheia o user).
  async signup({ nomeEmpresa, nome, email, senha, cnpj, aceitoTermos, codigoIndicacao }) {
    const params = new URLSearchParams();
    params.append('nome_empresa', nomeEmpresa);
    params.append('nome', nome);
    params.append('email', email);
    params.append('senha', senha);
    if (cnpj) params.append('cnpj', cnpj);
    params.append('aceito_termos', aceitoTermos ? 'true' : 'false');
    // #169 — código de indicação do consultor parceiro (opcional). Inválido é
    // ignorado silenciosamente pelo backend; nunca barra o cadastro.
    if (codigoIndicacao) params.append('codigo_indicacao', codigoIndicacao);

    const response = await fetch(`${API_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Erro ao criar conta');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }

    const user = await this.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(user));

    return user;
  }

  // Logout — Sessão 17: invalida o token no SERVIDOR (best-effort) além
  // de limpar o storage local. POST /auth/logout incrementa o
  // token_version do usuário no backend, derrubando o JWT de fato.
  // Limpa o localStorage primeiro (UX: desloga na hora); o aviso ao
  // servidor é best-effort e não bloqueia a saída.
  async logout() {
    const token = this.getToken();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
    if (token) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (e) {
        // Rede indisponível — o token expira sozinho; storage já limpo.
      }
    }
  }

  // #271 — Encerramento de sessão por 401 ("sessão expirada"), reutilizável.
  //
  // Espelha EXATAMENTE o que os interceptors de auth (api.js / fetchInterceptor.js)
  // já fazem ao detectar uma sessão irrecuperável: preserva o rascunho (#145),
  // marca a flag de sessão expirada (a tela de login mostra o aviso #145), limpa
  // o storage (logout local, best-effort no servidor) e volta pra tela de login.
  //
  // Por que existe: alguns fluxos (ex.: cascata IA via axios, edição de campo
  // base via fetch) precisam, ALÉM do que o interceptor faz, parar o seu próprio
  // spinner e dar feedback humano ANTES de o redirect navegar. Eles chamam isto
  // pra acionar a MESMA mecânica de encerramento — sem reimplementar nem
  // divergir do comportamento dos interceptors.
  handleSessionExpired() {
    flushDraftToStorage(); // #145 — preserva rascunho + marca a flag de sessão expirada
    this.logout();
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }

  // Refresh token (Sessão 17 C) — guardado no localStorage pelo login.
  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  // Troca o refresh token por um novo access token (POST /auth/refresh).
  //
  // Sessão 19 — Single-flight COMPARTILHADO entre fetchInterceptor e axios
  // interceptor:
  //   Antes, fetchInterceptor tinha seu próprio `_refreshing` local
  //   (utils/fetchInterceptor.js:28). Mas requisições axios em paralelo
  //   passavam por outro caminho. Resultado: usuário com NC aberta tinha
  //   2 polls fetch + N saves axios simultâneos disparando 2 refresh
  //   independentes, podendo invalidar um ao outro.
  //   Agora o single-flight vive AQUI no authService (instância singleton):
  //   ambos os interceptors compartilham a MESMA promise pendente. Cada
  //   chamador aguarda a promise existente em vez de iniciar outra.
  //
  // Lança erro se o refresh falhar (refresh token expirado/revogado) —
  // os interceptors tratam isso encerrando a sessão.
  refreshAccessToken() {
    if (this._refreshingPromise) {
      return this._refreshingPromise;
    }
    this._refreshingPromise = this._doRefresh().finally(() => {
      this._refreshingPromise = null;
    });
    return this._refreshingPromise;
  }

  async _doRefresh() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('Sem refresh token');
    }
    const params = new URLSearchParams();
    params.append('refresh_token', refreshToken);

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params,
    });
    if (!response.ok) {
      throw new Error('Refresh token inválido ou expirado');
    }
    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    // Sessão 19 — se o backend evoluir pra rotacionar refresh_token na
    // resposta do /auth/refresh, persistimos o novo aqui. Hoje o backend
    // só retorna access_token (auth_routes.py:181-184), então `data.refresh_token`
    // vem undefined e o if é no-op.
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    return data.access_token;
  }

  // Solicita link de redefinição de senha (POST /auth/forgot-password).
  // O backend responde sempre de forma genérica (anti-enumeração).
  async forgotPassword(email) {
    const params = new URLSearchParams();
    params.append('email', email);

    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Erro ao solicitar redefinição de senha');
    }
    return await response.json();
  }

  // Redefine a senha com o token recebido por e-mail
  // (POST /auth/reset-password). Campo do backend: `nova_senha`.
  async resetPassword(token, novaSenha) {
    const params = new URLSearchParams();
    params.append('token', token);
    params.append('nova_senha', novaSenha);

    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Erro ao redefinir a senha');
    }
    return await response.json();
  }

  // #81 — prévia do convite de equipe (email/empresa/papel) a partir do token.
  async previewInvite(token) {
    const response = await fetch(
      `${API_URL}/auth/accept-invite?token=${encodeURIComponent(token)}`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Convite inválido ou expirado.');
    }
    return await response.json();
  }

  // #81 — aceita o convite: cria o usuário no tenant + já faz auto-login
  // (persiste tokens, busca /auth/me, cacheia o user) — mesmo fluxo do signup.
  async acceptInvite(token, nome, senha) {
    const params = new URLSearchParams();
    params.append('token', token);
    params.append('nome', nome);
    params.append('senha', senha);

    const response = await fetch(`${API_URL}/auth/accept-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Não foi possível aceitar o convite.');
    }
    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    const user = await this.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  }

  // Obter token
  getToken() {
    return localStorage.getItem('token');
  }

  // Obter usuário atual
  async getCurrentUser() {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      this.logout();
      return null;
    }

    return await response.json();
  }

  // Self-edit do perfil (TD-followup-edicao-perfil-usuario Sessão 23).
  // PUT /auth/me — atualiza nome próprio. Retorna user atualizado.
  async updateMe(nome) {
    const token = this.getToken();
    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ nome }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.detail || 'Erro ao atualizar perfil');
      err.status = response.status;
      throw err;
    }
    const updated = await response.json();
    // Atualizar cache local
    localStorage.setItem('user', JSON.stringify(updated));
    return updated;
  }

  // Self-service: troca da própria senha. Exige senha atual.
  // POST /auth/change-password — retorna 204 (No Content).
  async changePassword(senhaAtual, novaSenha) {
    const token = this.getToken();
    const response = await fetch(`${API_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ senha_atual: senhaAtual, nova_senha: novaSenha }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.detail || 'Erro ao trocar senha');
      err.status = response.status;
      throw err;
    }
    // 204 No Content — sucesso
  }

  // Obter usuário do localStorage
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Verificar se está autenticado
  isAuthenticated() {
    return !!this.getToken();
  }

  // Verificar role
  hasRole(role) {
    const user = this.getUser();
    return user && user.role === role;
  }

  // Verificar se é auditor
  isAuditor() {
    return this.hasRole('auditor');
  }

  // Verificar se é admin
  isAdmin() {
    return this.hasRole('admin');
  }

  // Adicionar token nas requisições
  getAuthHeaders() {
    const token = this.getToken();
    return token ? {
      'Authorization': `Bearer ${token}`
    } : {};
  }
}

const authService = new AuthService();
export default authService;
